import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { CAP } from '../utils/permissions';
import { usePermissions, ReadOnlyNotice } from '../utils/PermissionContext';
import { authHeaders } from '../utils/apiAuth';
import { errorText } from '../utils/apiErrors';

const VAT_DEBOUNCE_MS = 600;
const VAT_IDLE = { status: 'idle', data: null };

const isDataMatch = (inputStr, apiStr) => {
    const squishedInput = (inputStr || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const squishedApi = (apiStr || '').toLowerCase().replace(/[^a-z0-9]/g, '');

    if (squishedApi.includes(squishedInput) || squishedInput.includes(squishedApi)) {
        return true;
    }

    const tokenize = (str) => {
        return (str || '').toLowerCase()
            .split(/[^a-z0-9]+/)
            .filter(Boolean)
            .map(t => t.replace(/^0+(\d)/, '$1'));
    };

    const inputTokens = tokenize(inputStr);
    const apiTokens = tokenize(apiStr);

    if (inputTokens.length === 0 || apiTokens.length === 0) return false;

    let apiTokensFound = 0;
    for (const t of apiTokens) {
        if (squishedInput.includes(t)) apiTokensFound++;
    }

    let inputTokensFound = 0;
    for (const t of inputTokens) {
        if (squishedApi.includes(t)) inputTokensFound++;
    }

    return (apiTokensFound / apiTokens.length >= 0.8) || (inputTokensFound / inputTokens.length >= 0.8);
};


function ProfileDetails({ t }) {
    // POST /api/customers/<id> requires writeCustomerProfile, so without it the
    // form is a read-only view of the record rather than an editor whose Save
    // is guaranteed to 403.
    const { can } = usePermissions();
    const canEditProfile = can(CAP.WRITE_CUSTOMER_PROFILE);
    const apiUrl = process.env.REACT_APP_API_URL || '';

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        customerName: '',
        address1: '',
        address2: '',
        country: '',
        emergencyEmail: '',
        vatNumber: '',
        language: 'nl',
    });
    const [initialData, setInitialData] = useState(null);
    const [emailChips, setEmailChips] = useState([]);
    const [emailInput, setEmailInput] = useState('');
    const [countries, setCountries] = useState([]);
    const [vatValidation, setVatValidation] = useState(VAT_IDLE);
    const vatDebounceRef = useRef(null);

    const getCustomerId = useCallback(() => {
        const storedCustomer = localStorage.getItem('selectedCustomer');
        if (storedCustomer) {
            const parsed = JSON.parse(storedCustomer);
            return parsed.customerId;
        }
        return null;
    }, []);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await fetch(`${apiUrl}/api/countries`, {
                    headers: authHeaders(),
                });
                if (!response.ok) throw new Error('Failed to fetch countries');
                const data = await response.json();
                setCountries(data);
            } catch (error) {
                console.error('Error fetching countries:', error);
            }
        };
        fetchCountries();
    }, [apiUrl]);

    useEffect(() => {
        const customerId = getCustomerId();
        if (!customerId) {
            setIsLoading(false);
            return;
        }

        const fetchProfile = async () => {
            setIsLoading(true);
            try {
                const response = await fetch(`${apiUrl}/api/customers/${customerId}`, {
                    headers: authHeaders(),
                });
                if (!response.ok) throw new Error('Failed to fetch profile');
                const data = await response.json();
                const profileData = {
                    customerName: data.customerName || data.name || '',
                    address1: data.address1 || '',
                    address2: data.address2 || '',
                    country: data.country || '',
                    contactEmail: data.contactEmail || '',
                    vatNumber: data.vatNumber || '',
                    emergencyEmail: data.emergencyEmail || '',
                    language: data.language || 'nl',
                };
                setFormData({
                    customerName: profileData.customerName,
                    address1: profileData.address1,
                    address2: profileData.address2,
                    country: profileData.country,
                    vatNumber: profileData.vatNumber,
                    emergencyEmail: profileData.emergencyEmail,
                    language: profileData.language,
                });
                setInitialData(profileData);
                const emailStr = profileData.contactEmail;
                if (emailStr) {
                    setEmailChips(emailStr.split(',').map(e => e.trim()).filter(Boolean));
                } else {
                    setEmailChips([]);
                }

                if (profileData.vatNumber) {
                    validateVatNumber(profileData.vatNumber);
                }
            } catch (error) {
                console.error('Error fetching profile:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiUrl, getCustomerId]);

    const validateVatNumber = useCallback(async (vatNumber) => {
        const sanitized = vatNumber.replace(/[\s.\-,]/g, '');
        if (sanitized.length < 5) {
            setVatValidation(VAT_IDLE);
            return;
        }

        setVatValidation({ status: 'validating', data: null });

        try {
            const response = await fetch(`${apiUrl}/api/validate-vat/${encodeURIComponent(sanitized)}`, {
                headers: authHeaders(),
            });

            if (response.status === 503) {
                setVatValidation({ status: 'error', data: null });
                return;
            }

            if (!response.ok) {
                setVatValidation({ status: 'error', data: null });
                return;
            }

            const data = await response.json();
            if (data.valid) {
                setVatValidation({ status: 'valid', data });
            } else {
                setVatValidation({ status: 'invalid', data });
            }
        } catch (error) {
            console.error('VAT validation error:', error);
            setVatValidation({ status: 'error', data: null });
        }
    }, [apiUrl]);

    const handleVatChange = useCallback((e) => {
        const { value } = e.target;
        setFormData((prev) => ({ ...prev, vatNumber: value }));

        if (vatDebounceRef.current) {
            clearTimeout(vatDebounceRef.current);
        }

        const trimmed = value.replace(/[\s.\-,]/g, '');
        if (!trimmed) {
            setVatValidation(VAT_IDLE);
            return;
        }

        vatDebounceRef.current = setTimeout(() => {
            validateVatNumber(value);
        }, VAT_DEBOUNCE_MS);
    }, [validateVatNumber]);

    useEffect(() => {
        return () => {
            if (vatDebounceRef.current) {
                clearTimeout(vatDebounceRef.current);
            }
        };
    }, []);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        const customerId = getCustomerId();
        if (!customerId) return;

        const hasVatInput = formData.vatNumber.replace(/[\s.\-,]/g, '').length > 0;
        if (hasVatInput && vatValidation.status === 'invalid') {
            toast.error(t.vat_invalid || 'VAT number is not valid');
            return;
        }
        if (hasVatInput && vatValidation.status === 'validating') {
            toast.error(t.vat_validating || 'Validating VAT number...');
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                customerName: formData.customerName,
                address1: formData.address1,
                address2: formData.address2,
                country: formData.country,
                vatNumber: formData.vatNumber,
                emergencyEmail: formData.emergencyEmail,
                language: formData.language,
                contactEmail: [...emailChips, ...(emailInput.trim() ? [emailInput.trim()] : [])].join(', '),
            };

            const response = await fetch(`${apiUrl}/api/customers/${customerId}`, {
                method: 'POST',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(errorText(data, t, t.profile_save_error || 'Failed to save profile'));
            }

            const savedData = {
                ...formData,
                contactEmail: payload.contactEmail
            };
            setInitialData(savedData);
            toast.success(t.profile_saved || 'Profile saved successfully');
        } catch (error) {
            console.error('Error saving profile:', error);
            toast.error(error.message || t.profile_save_error || 'Failed to save profile');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        if (!initialData) return;

        setFormData({
            customerName: initialData.customerName,
            address1: initialData.address1,
            address2: initialData.address2,
            country: initialData.country,
            vatNumber: initialData.vatNumber,
            emergencyEmail: initialData.emergencyEmail,
            language: initialData.language,
        });

        const emailStr = initialData.contactEmail || '';
        if (emailStr) {
            setEmailChips(emailStr.split(',').map(e => e.trim()).filter(Boolean));
        } else {
            setEmailChips([]);
        }
        setEmailInput('');

        const trimmedVat = (initialData.vatNumber || '').replace(/[\s.\-,]/g, '');
        if (trimmedVat.length >= 5) {
            validateVatNumber(initialData.vatNumber);
        } else {
            setVatValidation(VAT_IDLE);
        }

        toast.success(t.changes_discarded || 'Changes discarded');
    };

    const renderVatStatus = () => {
        if (vatValidation.status === 'idle') return null;

        if (vatValidation.status === 'validating') {
            return (
                <div className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-500">
                    <svg className="animate-spin h-3.5 w-3.5 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span>{t.vat_validating || 'Validating VAT number...'}</span>
                </div>
            );
        }

        if (vatValidation.status === 'valid') {
            const companyName = vatValidation.data?.name;
            const addressStr = vatValidation.data?.strAddress?.replace(/\n/g, ', ');

            const isNameMatch = isDataMatch(formData.customerName, companyName);
            const isAddressMatch = isDataMatch((formData.address1 || '') + ' ' + (formData.address2 || ''), vatValidation.data?.strAddress);
            
            const hasMismatch = !isNameMatch || !isAddressMatch;

            return (
                <div className="mt-1.5 flex flex-col gap-1.5">
                    <div className="flex items-start gap-1.5 text-xs text-green-700">
                        <svg className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span>
                            {t.vat_valid || 'VAT number is valid'}
                            {companyName && <span className="text-green-600"> — {companyName}</span>}
                            {addressStr && <span className="text-green-500">, {addressStr}</span>}
                        </span>
                    </div>
                    {hasMismatch && (
                        <div className="flex items-start gap-1.5 text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                            <svg className="h-4 w-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <span>
                                {t.vat_mismatch_warning || 'Warning: Company name or address differs from VAT registry'}
                            </span>
                        </div>
                    )}
                </div>
            );
        }

        if (vatValidation.status === 'invalid') {
            return (
                <div className="mt-1.5 flex items-center gap-1.5 text-xs text-red-600">
                    <svg className="h-3.5 w-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    <span>{t.vat_invalid || 'VAT number is not valid'}</span>
                </div>
            );
        }

        if (vatValidation.status === 'error') {
            return (
                <div className="mt-1.5 flex items-center gap-1.5 text-xs text-amber-600">
                    <svg className="h-3.5 w-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span>{t.vat_validation_error || 'VAT validation unavailable'}</span>
                </div>
            );
        }

        return null;
    };

    const saveDisabled = useMemo(() => {
        if (isSaving) return true;
        const hasVatInput = formData.vatNumber.replace(/[\s.\-,]/g, '').length > 0;
        if (hasVatInput && (vatValidation.status === 'invalid' || vatValidation.status === 'validating')) {
            return true;
        }
        return false;
    }, [isSaving, formData.vatNumber, vatValidation.status]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-gray-600">{t.loading}</div>
            </div>
        );
    }

    return (
        <div>
            <h2 className="text-2xl font-bold mb-6">{t.profile || 'Profile'}</h2>

            {/* Keyed on writeCustomerProfile, not adminWrite: User and every
                customer may edit this form, so the default admin-screen notice
                would be wrong here. Read-only and prospects get the fieldset
                disabled above, and this says why. */}
            <ReadOnlyNotice
                t={t}
                capability={CAP.WRITE_CUSTOMER_PROFILE}
                message={t?.profile_readonly_notice
                    || 'Your account can view these details but not change them. Ask an administrator if something needs updating.'}
            />

            <form onSubmit={handleSave} className="bg-white rounded-lg shadow p-6 space-y-6">
                {/* One fieldset rather than fifteen `disabled` props: everything
                    inside this form is part of the same write, so disabling the
                    lot is both correct and the smaller change. Tailwind's
                    preflight strips the fieldset's own border and padding, so
                    the layout is unchanged; space-y-6 moves onto it because it
                    is now the form's single child. */}
                <fieldset disabled={!canEditProfile} className="space-y-6 min-w-0">
                <div>
                    <label htmlFor="customerName" className="block text-sm font-medium text-gray-700 mb-1">
                        {t.customer_name || 'Customer Name'}
                    </label>
                    <input
                        type="text"
                        id="customerName"
                        name="customerName"
                        value={formData.customerName}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                        placeholder={t.customer_name_placeholder || 'Enter company or customer name'}
                    />
                </div>

                <div>
                    <label htmlFor="address1" className="block text-sm font-medium text-gray-700 mb-1">
                        {t.address1 || 'Address Line 1'}
                    </label>
                    <input
                        type="text"
                        id="address1"
                        name="address1"
                        value={formData.address1}
                        onChange={handleChange}
                        required
                        className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                        placeholder={t.address1_placeholder || 'Unit and street name'}
                    />
                </div>

                <div>
                    <label htmlFor="address2" className="block text-sm font-medium text-gray-700 mb-1">
                        {t.address2 || 'Address Line 2'}
                    </label>
                    <input
                        type="text"
                        id="address2"
                        name="address2"
                        value={formData.address2}
                        onChange={handleChange}
                        required
                        className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                        placeholder={t.address2_placeholder || 'Postcode and city'}
                    />
                </div>

                <div>
                    <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                        {t.country || 'Country'}
                    </label>
                    <select
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                    >
                        <option value="">{t.select_country || 'Select a country'}</option>
                        {countries.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label htmlFor="vatNumber" className="block text-sm font-medium text-gray-700 mb-1">
                        {t.vat_number || 'VAT Number'}
                    </label>
                    <input
                        type="text"
                        id="vatNumber"
                        name="vatNumber"
                        value={formData.vatNumber}
                        onChange={handleVatChange}
                        className={`w-full border rounded-md p-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${
                            vatValidation.status === 'valid'
                                ? 'border-green-400'
                                : vatValidation.status === 'invalid'
                                    ? 'border-red-400'
                                    : vatValidation.status === 'error'
                                        ? 'border-amber-400'
                                        : 'border-gray-300'
                        }`}
                        placeholder={t.vat_number_placeholder || 'VAT number (e.g., NL123456789B01)'}
                    />
                    {renderVatStatus()}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.contact_email || 'Contact Email'}
                    </label>
                    <div
                        className="w-full min-h-[48px] border border-gray-300 rounded-md p-2 text-sm focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500 transition flex flex-wrap gap-2 items-center cursor-text"
                        onClick={() => document.getElementById('contactEmailInput').focus()}
                    >
                        {emailChips.map((email, index) => (
                            <span
                                key={index}
                                className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-800 text-xs font-medium px-2.5 py-1.5 rounded-full"
                            >
                                {email}
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setEmailChips(prev => prev.filter((_, i) => i !== index));
                                    }}
                                    className="ml-0.5 text-indigo-600 hover:text-indigo-900 focus:outline-none"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </span>
                        ))}
                        <input
                            type="text"
                            id="contactEmailInput"
                            value={emailInput}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val.includes(',')) {
                                    const parts = val.split(',');
                                    const newEmails = parts.slice(0, -1).map(s => s.trim()).filter(Boolean);
                                    setEmailChips(prev => [...prev, ...newEmails.filter(em => !prev.includes(em))]);
                                    setEmailInput(parts[parts.length - 1]);
                                } else {
                                    setEmailInput(val);
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Backspace' && emailInput === '' && emailChips.length > 0) {
                                    setEmailChips(prev => prev.slice(0, -1));
                                }
                                if (e.key === 'Enter' && emailInput.trim()) {
                                    e.preventDefault();
                                    const trimmed = emailInput.trim();
                                    if (!emailChips.includes(trimmed)) {
                                        setEmailChips(prev => [...prev, trimmed]);
                                    }
                                    setEmailInput('');
                                }
                            }}
                            className="flex-1 min-w-[120px] outline-none bg-transparent text-sm p-1"
                            placeholder={emailChips.length === 0 ? (t.contact_email_placeholder || 'billing@example.com, info@example.com') : ''}
                        />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                        {t.contact_email_hint || 'Separate multiple emails with commas'}
                    </p>
                </div>

                <div>
                    <label htmlFor="emergencyEmail" className="block text-sm font-medium text-gray-700 mb-1">
                        {t.emergency_email || 'Emergency Email'}
                    </label>
                    <input
                        type="email"
                        id="emergencyEmail"
                        name="emergencyEmail"
                        value={formData.emergencyEmail}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                        placeholder={t.emergency_email_placeholder || 'emergency@example.com'}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                        {t.emergency_email_hint || 'Emergency contact email that is outside of your domain.'}
                    </p>
                </div>

                <div>
                    <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-1">
                        {t.language || 'Language'}
                    </label>
                    <select
                        id="language"
                        name="language"
                        value={formData.language}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                    >
                        <option value="en">{t.english || 'English'}</option>
                        <option value="nl">{t.dutch || 'Dutch'}</option>
                    </select>
                </div>

                {canEditProfile && <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-200"
                    >
                        {t.cancel || 'Cancel'}
                    </button>
                    <button
                        type="submit"
                        disabled={saveDisabled}
                        className={`px-6 py-2 text-sm font-medium text-white rounded-md transition duration-200 ${saveDisabled
                            ? 'bg-indigo-400 cursor-not-allowed'
                            : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                            }`}
                    >
                        {isSaving ? (t.saving || 'Saving...') : (t.save || 'Save')}
                    </button>
                </div>}
                </fieldset>
            </form>
        </div>
    );
}

export default ProfileDetails;
